import { useState } from "react";
import { Info } from "lucide-react";

/**
 * InfoTooltip - small "(i)" hover tooltip used across Admin to explain what a
 * field is, where its value is sourced from, and where it syncs to (per master
 * spec §127, §129). Pass `where_synced` to describe the runtime surfaces that
 * mirror this Admin field.
 *
 * Usage:
 *   <InfoTooltip
 *     label="Display Name"
 *     description="The name shown across /me, host dashboard, and preference cards."
 *     source="users.display_name"
 *     where_synced={["/me header", "Host Dashboard avatar", "Preference Card"]}
 *   />
 */
export function InfoTooltip({ label, description, source, where_synced = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="info-tooltip"
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-gray-700 transition-colors"
        aria-label={`Info: ${label || ""}`}
        data-testid="info-tooltip-trigger"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          className="absolute left-5 top-1/2 -translate-y-1/2 z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none"
          data-testid="info-tooltip-content"
        >
          {label && <span className="block font-semibold mb-1">{label}</span>}
          {description && <span className="block text-gray-200 mb-2 leading-snug">{description}</span>}
          {source && (
            <span className="block text-[10px] text-gray-400">
              <span className="uppercase tracking-wider mr-1">Source:</span>
              <code className="bg-gray-800 px-1 py-0.5 rounded text-emerald-300">{source}</code>
            </span>
          )}
          {where_synced.length > 0 && (
            <span className="block text-[10px] text-gray-400 mt-1">
              <span className="uppercase tracking-wider">Syncs to:</span>{" "}
              {where_synced.join(" • ")}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export default InfoTooltip;
